import { z } from "zod";

/** The type a signal with no rendering of its own is emitted as. */
export const GENERIC_SIGNAL_STREAM_TYPE = "generic-signal";

/**
 * One SDK signal forwarded to the browser without a rendering of its own,
 * field-for-field from the agent runner's `generic_signal` IPC event.
 *
 * `signal` is the SDK's own words for what fired, namespaced by the message that
 * carried it (`system/<a subtype this build has never seen>`). The namespace is
 * kept all the way to the rendered label: two messages can carry the same word,
 * and a reader shown only the leaf would meet two unrelated signals as one row.
 *
 * `payload` is opaque — nothing between the runner and the render site reads it,
 * beyond the host stripping secrets from its string values — so a reader sees
 * that the signal happened and what it carried without anyone having designed a
 * treatment for it.
 *
 * Persisted as a row of its own, not inside a turn. The conversation-events
 * materializer folds this frame off the log and writes one row keyed by the log
 * entry it came from, so a conversation reopened tomorrow shows the signals its
 * runs reported, in the position they arrived. The row is display-only: the
 * reconstruction path drops it rather than describing our own telemetry to a
 * model.
 *
 * A row, rather than an ordered part on the turn, because a signal is true of the
 * RUN: the between-turns feed carries these with no turn open at all, and the
 * live reader already draws each one as its own row keyed by the same log entry —
 * so the two arrivals share one identity and collapse instead of doubling.
 */
export const GenericSignalStreamEventSchema = z.object({
  type: z.literal(GENERIC_SIGNAL_STREAM_TYPE),
  signal: z.string(),
  payload: z.record(z.string(), z.unknown())
});

/** The type a context compaction is emitted as. */
export const CONTEXT_COMPACTED_STREAM_TYPE = "context-compacted";

/**
 * The model's context was rewritten mid-run, field-for-field from the agent
 * runner's `context_compacted` IPC event.
 *
 * `trigger` separates a reader's own `/compact` from the SDK hitting its
 * threshold; the token pair is the magnitude of what was given up. A reader who
 * cannot see this cannot tell a compacted answer from a wrong one, which is the
 * whole reason it crosses.
 *
 * Nothing draws a bespoke row for it today, and that is a resting state rather
 * than a gap: typing a signal and rendering it are separate decisions, and only
 * the first gates a consumer's ability to act on the fields.
 *
 * Every measurement but the trigger is optional, mirroring the runner hop for
 * the reason `ContextCompactedSchema` gives there: the SDK supplies each one
 * only where the compaction path measured it, and a compaction that reported
 * none of them is still a compaction the reader has to know about.
 */
export const ContextCompactedStreamEventSchema = z.object({
  type: z.literal(CONTEXT_COMPACTED_STREAM_TYPE),
  trigger: z.enum(["manual", "auto"]),
  preTokens: z.number().int().nonnegative().optional(),
  postTokens: z.number().int().nonnegative().optional(),
  durationMs: z.number().int().nonnegative().optional()
});

/** The type a slash-command palette change is emitted as. */
export const COMMANDS_CHANGED_STREAM_TYPE = "commands-changed";

/**
 * One slash command, field-for-field from the agent runner's
 * `commands_changed` IPC event.
 */
export const CommandsChangedStreamCommandSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  argumentHint: z.string().optional(),
  aliases: z.array(z.string()).optional()
});

/**
 * The session's slash-command palette changed mid-run, carrying the whole list
 * after the change.
 *
 * REPLACE semantics, as on the runner hop: a reader swaps its cached palette
 * for this payload rather than merging, or it keeps commands that were removed.
 * An empty list is a legitimate replacement — every command gone — and is why
 * the field is required rather than omitted when it holds nothing.
 *
 * Richer than what `session-config` carries for the same subject: that member
 * reports bare command names once per turn, this one reports each command's
 * description, argument hint and aliases at the moment the palette moves.
 *
 * Nothing draws a bespoke row for it today, and that is a resting state rather
 * than a gap: typing a signal and rendering it are separate decisions, and only
 * the first gates a consumer's ability to act on the fields.
 */
export const CommandsChangedStreamEventSchema = z.object({
  type: z.literal(COMMANDS_CHANGED_STREAM_TYPE),
  commands: z.array(CommandsChangedStreamCommandSchema)
});

/** The type a model refusal and its withdrawal are emitted as. */
export const MODEL_REFUSAL_STREAM_TYPE = "model-refusal";

/**
 * The model refused a request, and the frames it had already produced for it are
 * withdrawn — field-for-field from the agent runner's `model_refusal` IPC event.
 *
 * **`retractedBlockIds` is the half a reader acts on.** Every block named there
 * is to be removed from the transcript on receipt: a refusal that was retried on
 * a fallback model has a replacement answer arriving behind it, and a reader
 * that keeps both shows a refused half-answer beside the real one with nothing
 * marking which is which. Removal is idempotent — a block id that is unknown or
 * already gone is a no-op — so the same id arriving twice, once per producer, is
 * the designed case and not a duplicate to guard against.
 *
 * `unresolvedRetractedUuids` names the frames the runner could not translate —
 * empty in the ordinary case. It carries the one fact the resolved list cannot:
 * a withdrawal asked for and not carried out, told apart from a refusal that had
 * nothing to withdraw, which is the same empty `retractedBlockIds` with nothing
 * beside it.
 *
 * `requestId` and `sdkSessionId` are absent on purpose: both are operator
 * correlation handles for a log, and the host keeps them rather than handing a
 * browser an internal id it has no reader for.
 *
 * `notice` is what a refusal with no fallback has instead of an empty turn — the
 * SDK's own prose for why the answer is not coming. `refusalExplanation` is the
 * API's, and is display-only: it drifts, and nothing may branch on it.
 *
 * Only `source` and the two lists are guaranteed. The per-frame supersede path
 * carries no model, no category and no prose, and older CLIs omit several of the
 * rest — and the shapes are strict, so requiring a field the emitter left out
 * would refuse the whole payload and lose the withdrawal it was carrying.
 */
export const ModelRefusalStreamEventSchema = z.object({
  type: z.literal(MODEL_REFUSAL_STREAM_TYPE),
  source: z.enum(["supersede", "fallback", "no_fallback"]),
  retractedBlockIds: z.array(z.string().min(1)),
  unresolvedRetractedUuids: z.array(z.string().min(1)),
  originalModel: z.string().min(1).optional(),
  fallbackModel: z.string().min(1).optional(),
  scope: z.enum(["session", "local"]).optional(),
  refusalCategory: z.string().min(1).optional(),
  refusalExplanation: z.string().min(1).optional(),
  notice: z.string().min(1).optional(),
  refusedUserMessageUuid: z.string().min(1).optional()
});

/** The type a memory recall is emitted as. */
export const MEMORY_RECALL_STREAM_TYPE = "memory-recall";

/**
 * The memory-recall supervisor surfaced stored memories into the turn,
 * field-for-field from the agent runner's `memory_recall` IPC event.
 *
 * What crosses is provenance: an answer drawn from a memory is otherwise
 * indistinguishable from one the model produced unaided, and the recalled
 * material appears nowhere else in the transcript.
 *
 * **`content` crosses rather than being withheld as bulk.** For a `synthesize`
 * entry and for `organization` scope it is the only identifying information the
 * message carries — `path` is a `<synthesis:DIR>` sentinel or an https URL there
 * — so a browser event without it names memories a reader cannot recognise.
 *
 * A body arrives already bounded: the runner truncates one past
 * `MEMORY_RECALL_CONTENT_MAX_CHARS` before emitting, and the runner-hop schema
 * declares that cap. Restating it here would re-check a bound nothing between
 * the two hops can violate, and couple this contract to that tree to do it.
 *
 * It is deliberately not run through the broad secret redaction the generic
 * carrier applies. That argument turns on a payload being a machine-generated
 * field bag; a memory body is user-authored prose, the class those greedy
 * patterns are withheld from everywhere else on this path because they mangle it.
 *
 * Nothing draws a bespoke row for it today, and that is a resting state rather
 * than a gap: typing a signal and rendering it are separate decisions, and only
 * the first gates a consumer's ability to act on the fields.
 *
 * `sdkSessionId` is absent on purpose — it is the host's correlation handle for a
 * runner line, and names a sandbox-side identity no browser has a reader for.
 */
export const MemoryRecallStreamEventSchema = z.object({
  type: z.literal(MEMORY_RECALL_STREAM_TYPE),
  mode: z.enum(["select", "synthesize"]),
  memories: z.array(
    z.object({
      path: z.string(),
      scope: z.enum(["personal", "team", "organization"]),
      content: z.string().optional(),
      /**
       * Why this build shortened or withheld the body, where it did. Absent means
       * the body is exactly what the SDK sent — which for a file-backed `select`
       * entry is nothing, by design. It crosses because the renderer is the
       * reader that needs it: a cut body and a whole one are both strings.
       */
      boundedBy: z.enum(["entry-cap", "line-budget"]).optional()
    })
  )
});

/** The type a persisted-file batch receipt is emitted as. */
export const FILES_PERSISTED_STREAM_TYPE = "files-persisted";

/**
 * A batch of the turn's files was persisted, field-for-field from the agent
 * runner's `files_persisted` IPC event.
 *
 * `failed` is why this crosses at all: a file the model was told it had, that
 * never landed, is reported here and on no other frame — so a reader without it
 * sees an answer built on something absent, with nothing explaining the gap.
 *
 * `processedAt` is carried as the SDK wrote it, unparsed, and is optional for the
 * same reason. The shipped type states no format, so interpreting it would pick
 * between ISO-8601 and epoch-millis on no evidence and be wrong by decades on the
 * other reading — and a build that cannot read the value the SDK sent omits it
 * rather than costing the frame, which would take `failed` with it.
 *
 * Nothing draws a bespoke row for it today, which is a resting state rather
 * than a gap.
 *
 * `sdkSessionId` stops at the host, like every other correlation handle.
 */
export const FilesPersistedStreamEventSchema = z.object({
  type: z.literal(FILES_PERSISTED_STREAM_TYPE),
  files: z.array(
    z.object({
      filename: z.string(),
      fileId: z.string()
    })
  ),
  failed: z.array(
    z.object({
      filename: z.string(),
      error: z.string()
    })
  ),
  processedAt: z.string().optional()
});

/** The type a predicted follow-up prompt is emitted as. */
export const PROMPT_SUGGESTION_STREAM_TYPE = "prompt-suggestion";

/**
 * The model's guess at what the user will ask next, field-for-field from the
 * agent runner's `prompt_suggestion` IPC event.
 *
 * At most one per turn, and its absence is the ordinary case rather than a
 * fault: six documented conditions suppress it, and only the long-lived launch
 * asks for one at all. `PromptSuggestionSchema` on the runner hop carries why.
 *
 * `suggestion` is what a composer would be prefilled with, so it crosses as the
 * model wrote it. It arrives already bounded: the runner truncates past
 * `PROMPT_SUGGESTION_MAX_CHARS` before emitting, and the runner-hop schema
 * declares that cap. Restating it here would re-check a bound nothing between
 * the two hops can violate.
 *
 * Nothing draws a bespoke row for it today, and that is a resting state rather
 * than a gap: typing a signal and rendering it are separate decisions, and only
 * the first gates a consumer's ability to act on the fields.
 *
 * `sdkSessionId` stops at the host, like every other correlation handle.
 */
export const PromptSuggestionStreamEventSchema = z.object({
  type: z.literal(PROMPT_SUGGESTION_STREAM_TYPE),
  suggestion: z.string(),
  /**
   * Why the runner shortened the prediction, where it did. It crosses because
   * the renderer is the reader that needs it — offering a cut prediction as
   * though it were whole prefills a half-formed prompt, and a cut string and a
   * complete one are otherwise the same string.
   */
  boundedBy: z.enum(["entry-cap"]).optional()
});
